import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { trafficLight, fmtEUR, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { ArrowLeft, Plus, Building2, User, ChevronRight, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";
import ClientEditDialog from "../components/ClientEditDialog";
import ClientMiniGantt from "../components/ClientMiniGantt";

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data));
    api.get(`/projects`, { params: { client_id: id } }).then((r) => setProjects(r.data));
  };
  useEffect(() => { load(); }, [id]);

  const downloadReport = async () => {
    try {
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/clients/${id}/report.pdf`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error("errore");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (client?.name || "cliente").replace(/[^\w\s.-]/g, "").replace(/\s+/g, "_");
      a.href = url; a.download = `report_${safe}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report scaricato");
    } catch { toast.error("Errore download report"); }
  };

  if (!client) return <div className="p-10">Caricamento...</div>;

  return (
    <div className="p-8 lg:p-10 max-w-5xl space-y-6 fade-up">
      <button onClick={() => nav(-1)} className="mono-label flex items-center gap-1.5" data-testid="back-btn"><ArrowLeft className="h-3 w-3" /> Indietro</button>
      <header className="flex items-start justify-between border-b border-neutral-200 pb-6">
        <div>
          <div className="mono-label flex items-center gap-1.5">
            {client.type === "pa" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {client.type === "pa" ? "Pubblica Amministrazione" : "Cliente Privato"}
          </div>
          <h1 className="font-display text-4xl font-medium mt-2">{client.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadReport} data-testid="client-report-pdf"><FileText className="h-4 w-4 mr-1.5" />Report PDF</Button>
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-btn"><Pencil className="h-4 w-4 mr-1.5" />Modifica</Button>
          <Button onClick={() => nav(`/projects/new?client_id=${id}`)} data-testid="new-project-for-client" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" />Nuova gara</Button>
        </div>
      </header>
      <ClientEditDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-neutral-200 bg-white">
        {[["Email", client.email], ["Telefono", client.phone], ["P.IVA", client.vat], ["Città", client.city]].map(([l, v], i) => (
          <div key={l} className={`p-4 ${i > 0 ? "border-l border-neutral-200" : ""}`}>
            <div className="mono-label">{l}</div>
            <div className="text-sm mt-1 truncate">{v || "—"}</div>
          </div>
        ))}
      </div>

      {client.type === "pa" && (
        <div className="grid grid-cols-3 gap-0 border border-neutral-200 bg-white">
          {[["RUP", client.rup], ["Codice IPA", client.codice_ipa], ["Referente", client.contact_person]].map(([l, v], i) => (
            <div key={l} className={`p-4 ${i > 0 ? "border-l border-neutral-200" : ""}`}>
              <div className="mono-label">{l}</div>
              <div className="text-sm mt-1 truncate">{v || "—"}</div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="mono-label mb-3">Gare & Progetti ({projects.length})</div>
        {projects.length > 0 && (
          <div className="mb-5">
            <div className="mono-label mb-2">Timeline gare</div>
            <ClientMiniGantt projects={projects} />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {projects.map((p) => {
            const tl = trafficLight(p.deadline, p.delivered);
            return (
              <Link key={p.id} to={`/projects/${p.id}`} className="block p-4 border border-neutral-200 bg-white hover-lift" data-testid={`client-project-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium truncate">{p.title}</div>
                  <ChevronRight className="h-4 w-4 text-neutral-400" />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="font-mono text-sm font-semibold">{fmtEUR(p.parcella_calculated)}</div>
                  <div className={`px-2 py-0.5 border text-xs font-mono ${tl.className}`}>{tl.label}</div>
                </div>
                <div className="mono-label mt-1">{fmtDate(p.deadline)}</div>
              </Link>
            );
          })}
          {projects.length === 0 && <div className="col-span-full py-8 text-center text-sm text-neutral-500 border border-dashed">Nessuna gara ancora</div>}
        </div>
      </div>
    </div>
  );
}
